import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AccountLink } from "../components/resellers/AccountLink";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { fmtDate, fmtDateTime, fmtNum, isoToLocalInput, localInputToIso } from "../lib/format";
import { pdf, Document, Page, View, Text, Image, StyleSheet } from "@react-pdf/renderer";
import { ArrowLeft, FileText, Download, Send, ClipboardCheck, PenLine, Trash2, AlertTriangle, ChevronDown, Banknote, EyeOff, CalendarClock, X, Undo2, Pencil, Save, Info, Receipt, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import { useConfirm } from "../components/ConfirmDialog";
import { showPdfPreview } from "../components/PdfPreview";
import DeliveryCorrectionDialog from "../components/stock/DeliveryCorrectionDialog";
import { toast } from "sonner";
import { COMPANY_HEADER } from "../lib/company";

const STATUS_META = {
  BROUILLON: { label: "Brouillon", className: "bg-[#F0F0EB] text-[#575A57] border-[#E5E5E0]" },
  ENVOYE: { label: "Envoyé", className: "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]" },
  LIVRE: { label: "Livré", className: "bg-[#E5EFF8] text-[#2D5A87] border-[#C5D8E8]" },
  SIGNE: { label: "Signé", className: "bg-[#EAF4EE] text-[#1F4E3D] border-[#C5DBCF]" },
  ANNULE: { label: "Annulé", className: "bg-[#FCEEEA] text-[#C05C47] border-[#E8C1B4]" },
};

/** Découpe une adresse "Rue X\\nCH-1234 Ville" (ou legacy "Rue X, CH-1234 Ville")
 * en lignes individuelles pour le rendu Swiss-standard.
 */
const addressLines = (addr) => {
  if (!addr) return [];
  // Backend produit "\\n" séparateur — fallback "," + espace pour les legacy data
  return String(addr)
    .split(/\n|,\s/)
    .map((s) => s.trim())
    .filter(Boolean);
};

/**
 * Regroupe les lignes BL par (product_id, dlc) pour l'affichage client.
 * Le détail par lot reste accessible via la section « Détail traçabilité HACCP ».
 *
 * Ex : 8 × Boeuf basilic lot 290526 + 4 × Boeuf basilic lot 290526 (même DLC)
 *      → 1 ligne 12 × Boeuf basilic, DLC 25/05.
 * Ex : 8 × Boeuf basilic lot 290526 (DLC 25/05) + 4 × Boeuf basilic lot 020626 (DLC 28/05)
 *      → 2 lignes : 8 + 4 (cas rare, DLCs différentes).
 */
const groupLinesForBl = (lines) => {
  // Phase 98an — Agrégation stricte par produit pour affichage client (BL PDF + écran)
  // Un client veut voir "Poulet 2×" et pas 2 lignes séparées quand le stock a été
  // pioché sur 2 lots différents (mêmes DLC ou pas). La traçabilité HACCP est
  // préservée dans `source_lots` (détail lot/DLC accessible en interne).
  //
  // Règle de la DLC affichée : la MIN (date la plus courte) — c'est la date que
  // le client doit respecter pour consommer. C'est LA date légale à afficher
  // quand plusieurs DLC sont mélangées dans une même livraison.
  const minDate = (a, b) => {
    if (!a) return b;
    if (!b) return a;
    return a < b ? a : b;
  };
  const map = new Map();
  for (const ln of lines || []) {
    const key = ln.product_id || "";
    if (!map.has(key)) {
      map.set(key, {
        product_id: ln.product_id,
        product_name: ln.product_name,
        product_sku: ln.product_sku,
        unit: ln.unit,
        dlc: ln.dlc,
        production_date: ln.production_date,
        unit_price: ln.unit_price,
        quantity: 0,
        source_lots: [],
      });
    }
    const agg = map.get(key);
    agg.quantity += Number(ln.quantity) || 0;
    // DLC affichée = MIN de tous les lots (la plus courte)
    agg.dlc = minDate(agg.dlc, ln.dlc);
    // Date de production affichée = MIN (le plus ancien)
    agg.production_date = minDate(agg.production_date, ln.production_date);
    if (ln.lot_number) {
      agg.source_lots.push({
        lot_number: ln.lot_number,
        quantity: Number(ln.quantity) || 0,
        dlc: ln.dlc,
      });
    }
    // Si plusieurs lignes ont un unit_price différent, garde le premier non null
    if ((agg.unit_price === null || agg.unit_price === undefined) && ln.unit_price !== null && ln.unit_price !== undefined) {
      agg.unit_price = ln.unit_price;
    }
  }
  return Array.from(map.values());
};

const pdfStyles = StyleSheet.create({
  page: { padding: 36, fontFamily: "Helvetica", fontSize: 9, color: "#1A1C1A" },
  headerRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 },
  companyBlock: { flexDirection: "row", alignItems: "flex-start", gap: 12 },
  companyLogo: { width: 56, height: 56 },
  companyInfo: { flexDirection: "column" },
  companyName: { fontSize: 12, fontFamily: "Helvetica-Bold", marginBottom: 2, letterSpacing: 0.6 },
  companyMeta: { fontSize: 8, color: "#575A57", lineHeight: 1.4 },
  blBlock: { flexDirection: "column", alignItems: "flex-end" },
  blTitle: { fontSize: 18, fontFamily: "Helvetica-Bold", color: "#1F4E3D" },
  blRef: { fontSize: 11, marginTop: 4, color: "#575A57", fontFamily: "Helvetica-Bold" },
  blDate: { fontSize: 9, marginTop: 2, color: "#575A57" },
  parties: { flexDirection: "row", gap: 16, marginBottom: 18 },
  party: { flex: 1, padding: 12, backgroundColor: "#FAFAF7", borderRadius: 4 },
  partyLabel: { fontSize: 8, color: "#A3A6A3", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4 },
  partyName: { fontSize: 11, fontFamily: "Helvetica-Bold", marginBottom: 2 },
  partyText: { fontSize: 9, color: "#575A57", marginBottom: 1 },
  table: { borderTop: "1pt solid #E5E5E0", borderLeft: "1pt solid #E5E5E0", borderRight: "1pt solid #E5E5E0", marginBottom: 16 },
  th: { flexDirection: "row", backgroundColor: "#F4F4F0", padding: 6, borderBottom: "1pt solid #E5E5E0" },
  thCell: { fontFamily: "Helvetica-Bold", fontSize: 8, color: "#575A57", textTransform: "uppercase", letterSpacing: 0.5 },
  tr: { flexDirection: "row", padding: 6, borderBottom: "1pt solid #E5E5E0" },
  td: { fontSize: 9 },
  totalsRow: { flexDirection: "row", justifyContent: "flex-end", marginTop: 8, paddingTop: 6, borderTop: "1pt solid #1A1C1A" },
  notes: { marginTop: 14, padding: 10, backgroundColor: "#FAFAF7", borderLeft: "3pt solid #1F4E3D" },
  notesLabel: { fontSize: 8, color: "#A3A6A3", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 3 },
  signRow: { flexDirection: "row", marginTop: 32, gap: 16 },
  signBox: { flex: 1, padding: 12, borderTop: "1pt solid #1A1C1A" },
  signLabel: { fontSize: 8, color: "#575A57", marginBottom: 2 },
  signValue: { fontSize: 9, fontFamily: "Helvetica-Bold" },
  signEmpty: { fontSize: 9, color: "#A3A6A3", fontFamily: "Helvetica-Oblique" },
  footer: { position: "absolute", bottom: 24, left: 36, right: 36, fontSize: 7, color: "#A3A6A3", textAlign: "center", borderTop: "0.5pt solid #E5E5E0", paddingTop: 6 },
  // Phase 101cl (18/02/2026) — Adresse destinataire en position fenêtre B5/C5 (SN 351)
  debtorWindow: {
    position: "absolute",
    top: "48mm",
    left: "115mm",
    width: "85mm",
    height: "45mm",
    padding: 4,
  },
  debtorLabel: { fontSize: 6, color: "#A3A6A3", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 },
  debtorName: { fontSize: 10, fontFamily: "Helvetica-Bold", marginBottom: 1 },
  debtorLine: { fontSize: 9.5, color: "#1A1C1A", lineHeight: 1.35 },
});

export const BLDocument = ({ bl, settings, showPrices = true, isLaboratory = false }) => {
  // Phase 101fa — En mode Labo, une ligne PAR LOT (pas d'agrégation) pour
  // permettre au laboratoire d'identifier chaque échantillon avec son n° de lot.
  const displayLines = isLaboratory
    ? (bl.lines || []).map((ln) => ({ ...ln, source_lots: ln.lot_number ? [{ lot_number: ln.lot_number, quantity: ln.quantity, dlc: ln.dlc }] : [] }))
    : groupLinesForBl(bl.lines || []);
  return (
  <Document>
    <Page size="A4" style={pdfStyles.page}>
      <View style={pdfStyles.headerRow}>
        <View style={pdfStyles.companyBlock}>
          <Image src={`${window.location.origin}/logo-maisonthai-pdf.png`} style={pdfStyles.companyLogo} />
          <View style={pdfStyles.companyInfo}>
            <Image
              src={`${typeof window !== "undefined" ? window.location.origin : ""}/company-name-sayonara.png`}
              style={{ width: 119, height: 12, marginBottom: 2 }}
            />
            {(settings?.company_address || "").split("\n").filter(Boolean).map((line, i) => (
              <Text key={i} style={pdfStyles.companyMeta}>{line}</Text>
            ))}
          </View>
        </View>
        <View style={pdfStyles.blBlock}>
          <Text style={pdfStyles.blTitle}>BON DE LIVRAISON</Text>
          <Text style={pdfStyles.blRef}>N° {bl.reference}</Text>
          <Text style={pdfStyles.blDate}>Date livraison : {fmtDate(bl.delivery_date)}</Text>
          <Text style={pdfStyles.blDate}>Émis le : {fmtDate(bl.created_at)}</Text>
        </View>
      </View>

      <View style={pdfStyles.parties}>
        <View style={pdfStyles.party}>
          <Text style={pdfStyles.partyLabel}>Émetteur</Text>
          <Text style={pdfStyles.partyName}>{COMPANY_HEADER}</Text>
          <Text style={pdfStyles.partyText}>Dépôt source : {bl.from_distributor_name || "—"}</Text>
        </View>
      </View>

      {/* === Phase 101cl (18/02/2026) — Adresse destinataire en position fenêtre B5/C5 (SN 351) === */}
      <View style={pdfStyles.debtorWindow}>
        <Text style={pdfStyles.debtorName}>{bl.distributor_name || "—"}</Text>
        {bl.distributor_contact ? <Text style={pdfStyles.debtorLine}>{bl.distributor_contact}</Text> : null}
        {addressLines(bl.distributor_address).map((line, i) => (
          <Text key={i} style={pdfStyles.debtorLine}>{line}</Text>
        ))}
      </View>

      {/* Spacer pour ne pas chevaucher la fenêtre absolue (48mm + 45mm = 93mm depuis le haut) */}
      <View style={{ height: "35mm" }} />

      <View style={pdfStyles.table}>
        <View style={pdfStyles.th}>
          <Text style={[pdfStyles.thCell, { flex: 2.3 }]}>Produit</Text>
          {isLaboratory && (
            <Text style={[pdfStyles.thCell, { flex: 1.1 }]}>Lot</Text>
          )}
          <Text style={[pdfStyles.thCell, { flex: 0.8 }]}>SKU</Text>
          <Text style={[pdfStyles.thCell, { flex: 0.6, textAlign: "right" }]}>Qté</Text>
          <Text style={[pdfStyles.thCell, { flex: 0.4 }]}>Unité</Text>
          <Text style={[pdfStyles.thCell, { flex: 0.85, textAlign: "right" }]}>Date fab.</Text>
          <Text style={[pdfStyles.thCell, { flex: 0.85, textAlign: "right" }]}>DLC</Text>
          {showPrices && (
            <Text style={[pdfStyles.thCell, { flex: 0.7, textAlign: "right" }]}>PU CHF</Text>
          )}
        </View>
        {displayLines.map((ln, idx) => (
          <View key={idx} style={pdfStyles.tr}>
            <Text style={[pdfStyles.td, { flex: 2.3 }]}>{ln.product_name || "—"}</Text>
            {isLaboratory && (
              <Text style={[pdfStyles.td, { flex: 1.1, fontFamily: "Helvetica-Bold" }]}>{ln.lot_number || "—"}</Text>
            )}
            <Text style={[pdfStyles.td, { flex: 0.8 }]}>{ln.product_sku || "—"}</Text>
            <Text style={[pdfStyles.td, { flex: 0.6, textAlign: "right" }]}>{ln.quantity}</Text>
            <Text style={[pdfStyles.td, { flex: 0.4 }]}>{ln.unit || "pcs"}</Text>
            <Text style={[pdfStyles.td, { flex: 0.85, textAlign: "right" }]}>{ln.production_date ? fmtDate(ln.production_date) : "—"}</Text>
            <Text style={[pdfStyles.td, { flex: 0.85, textAlign: "right" }]}>{ln.dlc ? fmtDate(ln.dlc) : "—"}</Text>
            {showPrices && (
              <Text style={[pdfStyles.td, { flex: 0.7, textAlign: "right" }]}>{ln.unit_price ? Number(ln.unit_price).toFixed(2) : "—"}</Text>
            )}
          </View>
        ))}
      </View>

      <View style={pdfStyles.totalsRow}>
        <Text style={{ fontFamily: "Helvetica-Bold", fontSize: 10 }}>
          Total : {displayLines.reduce((acc, ln) => acc + (ln.quantity || 0), 0)} unité(s) ·
          {" "}{displayLines.length} ligne(s)
          {showPrices && (() => {
            // Phase 101bz (18/02/2026) — Arrondi commercial suisse (« Rappenrundung »)
            // au multiple de 0.05 CHF le plus proche. Cohérent avec finance/QR-Bill.
            const totalHT = displayLines.reduce((acc, ln) => acc + (Number(ln.quantity) || 0) * (Number(ln.unit_price) || 0), 0);
            const rounded = Math.round(totalHT * 20) / 20;
            return rounded > 0 ? ` · Total HT : ${rounded.toFixed(2)} CHF` : "";
          })()}
        </Text>
      </View>

      {bl.notes && (
        <View style={pdfStyles.notes}>
          <Text style={pdfStyles.notesLabel}>Notes</Text>
          <Text>{bl.notes}</Text>
        </View>
      )}

      <View style={pdfStyles.signRow}>
        <View style={pdfStyles.signBox}>
          <Text style={pdfStyles.signLabel}>Émis par</Text>
          <Text style={pdfStyles.signValue}>{COMPANY_HEADER}</Text>
          <Text style={pdfStyles.signLabel}>{fmtDateTime(bl.created_at)}</Text>
        </View>
        <View style={pdfStyles.signBox}>
          <Text style={pdfStyles.signLabel}>Signature client (réception)</Text>
          {bl.signed_by ? (
            <>
              <Text style={pdfStyles.signValue}>{bl.signed_by}</Text>
              {bl.signed_at && <Text style={pdfStyles.signLabel}>{fmtDateTime(bl.signed_at)}</Text>}
            </>
          ) : (
            <Text style={pdfStyles.signEmpty}>Nom et signature du réceptionnaire</Text>
          )}
        </View>
      </View>

      <Text style={pdfStyles.footer}>
        Document de livraison — La DLC fait foi pour la péremption. Détail traçabilité HACCP par lot disponible côté émetteur.
      </Text>
    </Page>
  </Document>
  );
};

export default function DeliveryNoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [bl, setBl] = useState(null);
  const [settings, setSettings] = useState(null);
  const [distributors, setDistributors] = useState([]);
  const [signOpen, setSignOpen] = useState(false);
  const [signedByInput, setSignedByInput] = useState("");
  // Phase 101fr — Attacher revendeur rétroactivement
  const [resellers, setResellers] = useState([]);
  const [attachDialogOpen, setAttachDialogOpen] = useState(false);
  const [attachForm, setAttachForm] = useState({ reseller_id: "", apply_prices: true, switch_distributor: false });
  const [attaching, setAttaching] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);
  const reload = useCallback(() => setRefreshKey((k) => k + 1), []);

  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadError(null);
        const [b, s, d] = await Promise.all([
          api.get(`/delivery-notes/${id}`),
          api.get("/settings"),
          api.get("/distributors"),
        ]);
        if (cancelled) return;
        setBl(b.data);
        setSettings(s.data);
        setDistributors(d.data || []);
        // Phase 101fr — Chargement paresseux des revendeurs pour le dialog attach
        api.get("/resellers")
          .then((r) => { if (!cancelled) setResellers(r.data || []); })
          .catch(() => {});
      } catch (e) {
        if (cancelled) return;
        // Phase 87s — log + message clair (auparavant la promesse échouée laissait
        // la page sur "Chargement…" indéfiniment, donnant l'impression que "le BL
        // ne s'ouvre pas").
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        let msg;
        if (status === 404) msg = `Bon de livraison introuvable (id : ${id}). Il a peut-être été supprimé.`;
        else if (status === 403) msg = "Vous n'avez pas les droits pour ouvrir ce BL.";
        else if (typeof detail === "string") msg = detail;
        else msg = e?.message || "Erreur réseau lors du chargement du BL.";
        console.error("[BL load]", e);
        setLoadError(msg);
      }
    })();
    return () => { cancelled = true; };
  }, [id, refreshKey]);
  // Alias gardé pour réduire le diff dans le reste du fichier
  const load = reload;

  const updateStatus = async (status) => {
    try {
      await api.put(`/delivery-notes/${id}`, { status });
      toast.success(`Statut mis à jour : ${STATUS_META[status]?.label}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  // Phase 101ea (Feb 2026) — Restauration d'un BL annulé par erreur.
  // L'annulation ne rend pas le stock, donc la restauration est safe : elle
  // remet le BL au statut « avant annulation » (SIGNE/LIVRE/BROUILLON selon
  // les timestamps déjà présents).
  const restoreCancelled = async () => {
    const ok = await confirm({
      title: "Restaurer le BL annulé ?",
      description: `Le BL ${bl?.reference} sera remis à son statut d'avant annulation. Aucun mouvement de stock n'a été rendu au moment de l'annulation, la restauration est donc sans effet de bord. L'audit d'annulation reste conservé.`,
      confirmText: "Restaurer",
      cancelText: "Ne pas restaurer",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/admin/delivery-notes/${id}/restore`);
      toast.success(`BL restauré au statut ${data.restored_to_status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la restauration");
    }
  };

  const executeTransfers = async () => {
    const isOut = bl?.distributor_id === "shop-online-virtual"
      || (destDistributor && destDistributor.type !== "AUTOMATIQUE");
    const title = isOut ? "Confirmer la sortie de stock ?" : "Exécuter les transferts de stock ?";
    const description = isOut
      ? `Les ${bl.lines.length} ligne(s) du BL ${bl.reference} seront déduites du dépôt source « ${bl.from_distributor_name || ""} » puis livrées directement chez ${bl.distributor_name || "le destinataire"}. Aucune barquette ne reste en stock chez le destinataire (la traçabilité HACCP est conservée via le BL). Action irréversible.`
      : `Les ${bl.lines.length} ligne(s) du BL ${bl.reference} créeront chacune un mouvement de transfert vers ${bl.distributor_name}. Cette action est irréversible.`;
    const confirmText = isOut ? "Confirmer la sortie" : "Exécuter les transferts";
    const ok = await confirm({ title, description, confirmText, cancelText: "Annuler" });
    if (!ok) return;
    try {
      await api.post(`/delivery-notes/${id}/execute-transfer`);
      toast.success(isOut ? "Sortie de stock exécutée · lots déduits" : "Transferts exécutés · stock mis à jour");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur — vérifiez que chaque ligne a un lot source");
    }
  };

  const sign = async () => {
    if (!signedByInput.trim()) { toast.error("Entrez le nom du signataire"); return; }
    try {
      await api.put(`/delivery-notes/${id}`, { status: "SIGNE", signed_by: signedByInput.trim() });
      toast.success("BL signé");
      setSignOpen(false);
      setSignedByInput("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const downloadPdf = async (showPrices = true) => {
    if (!bl) return;
    try {
      // Phase 101fa — Détecte si le distributeur destinataire est un laboratoire
      // d'analyse HACCP → active le layout dédié (1 ligne par lot + colonne Lot).
      const destDist = distributors.find((d) => d.id === bl.distributor_id);
      const isLaboratory = Boolean(destDist?.is_laboratory);
      const blob = await pdf(<BLDocument bl={bl} settings={settings} showPrices={showPrices} isLaboratory={isLaboratory} />).toBlob();
      const suffix = showPrices ? "" : "-sans-prix";
      const prefix = isLaboratory ? "BL-Labo-" : "";
      showPdfPreview(blob, `${prefix}${bl.reference}${suffix}.pdf`);
    } catch (e) {
      console.error(e);
      toast.error("Erreur génération PDF");
    }
  };

  const remove = async () => {
    const ok = await confirm({
      title: "Supprimer ce bon de livraison ?",
      description: `Le BL ${bl.reference} sera supprimé définitivement.`,
      confirmText: "Supprimer",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/delivery-notes/${id}`);
      toast.success("BL supprimé");
      navigate("/bons-livraison");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const totalQty = useMemo(() => (bl?.lines || []).reduce((acc, ln) => acc + (ln.quantity || 0), 0), [bl]);
  const hasLineWithoutLot = useMemo(() => (bl?.lines || []).some((ln) => !ln.lot_id), [bl]);
  const groupedLines = useMemo(() => groupLinesForBl(bl?.lines || []), [bl]);
  const [showHaccpDetail, setShowHaccpDetail] = useState(false);

  // Phase 101dm (Feb 2026) — Détermine si le BL est une sortie sèche (revendeur,
  // client B2B, boutique web) ou un vrai transfert vers un distributeur automatique.
  // Sert à contextualiser le label du bouton et le texte de confirmation.
  const destDistributor = useMemo(
    () => (distributors || []).find((d) => d.id === bl?.distributor_id) || null,
    [distributors, bl?.distributor_id],
  );
  const isOutDelivery = useMemo(() => {
    if (bl?.distributor_id === "shop-online-virtual") return true;
    if (!destDistributor) return false;
    return destDistributor.type !== "AUTOMATIQUE";
  }, [bl?.distributor_id, destDistributor]);

  // Phase 98ad — Édition des lignes du BL BROUILLON (retirer/ajuster avant exécution).
  // La CMD parente est automatiquement réalignée côté backend.
  const [editLinesMode, setEditLinesMode] = useState(false);
  const [draftLines, setDraftLines] = useState([]);
  const [savingLines, setSavingLines] = useState(false);
  // Phase 98ai — Stock disponible par lot (fetché à l'ouverture du mode édition)
  // pour bloquer l'utilisateur qui tenterait de dépasser le stock physique.
  const [lotStock, setLotStock] = useState({}); // {lot_id: {available, lot_number, product_name}}
  // Phase 98am — Tous les lots disponibles par produit pour permettre la sélection manuelle
  // du lot (override du FIFO auto). Utile quand un client demande une DLC homogène.
  const [productLots, setProductLots] = useState({}); // {product_id: [{id, lot_number, dlc, quantity, production_date}]}
  // Phase 101ct (Feb 2026) — Ajout de lignes en mode édition (uniquement BL libres).
  // BL lié à une CMD : bloqué pour éviter de désynchroniser la chaîne CMD/facture.
  const [isFreeBl, setIsFreeBl] = useState(false);
  const [linkedCoRef, setLinkedCoRef] = useState("");
  const [allProducts, setAllProducts] = useState([]);
  const [addLineOpen, setAddLineOpen] = useState(false);
  const [addForm, setAddForm] = useState({ product_id: "", lot_id: "", quantity: 1, unit_price: "" });
  const [addFormLots, setAddFormLots] = useState([]);
  const [addFormLotsLoading, setAddFormLotsLoading] = useState(false);
  const startEditLines = async () => {
    // Copie chaque ligne raw HACCP (une par lot) pour permettre un ajustement fin.
    const initial = (bl.lines || []).map((ln) => ({
      _key: ln.id || `${ln.product_id}-${ln.lot_id}-${Math.random()}`,
      lot_id: ln.lot_id || null,
      product_id: ln.product_id,
      product_name: ln.product_name || "",
      product_sku: ln.product_sku || "",
      lot_number: ln.lot_number || "",
      quantity: Number(ln.quantity) || 0,
      dlc: ln.dlc || null,
      production_date: ln.production_date || null,
      unit_price: ln.unit_price ?? null,
      unit: ln.unit || "pcs",
      notes: ln.notes || "",
      _deleted: false,
    }));
    setDraftLines(initial);
    setEditLinesMode(true);
    // Charge tous les lots disponibles pour les produits du BL (pour dropdown de sélection).
    const uniqueProductIds = [...new Set(initial.map((ln) => ln.product_id).filter(Boolean))];
    if (uniqueProductIds.length === 0) { setLotStock({}); setProductLots({}); return; }
    try {
      const allLotsArrays = await Promise.all(
        uniqueProductIds.map((pid) =>
          api.get(`/lots?product_id=${pid}`).then((r) => ({ pid, lots: r.data || [] })).catch(() => ({ pid, lots: [] })),
        ),
      );
      // Phase 98am — productLots : tous les lots avec quantity > 0 (ou déjà utilisés dans le BL)
      // triés par DLC croissant (FIFO d'abord, mais l'utilisateur peut choisir librement).
      const pLots = {};
      const stockMap = {};
      const currentLotIds = new Set(initial.map((ln) => ln.lot_id).filter(Boolean));
      allLotsArrays.forEach(({ pid, lots }) => {
        const usable = lots
          .filter((l) => Number(l.quantity || 0) > 0 || currentLotIds.has(l.id))
          .sort((a, b) => {
            const da = a.dlc || "9999-99-99";
            const db = b.dlc || "9999-99-99";
            return da.localeCompare(db);
          });
        pLots[pid] = usable.map((l) => ({
          id: l.id,
          lot_number: l.lot_number || "",
          dlc: l.dlc || null,
          quantity: Number(l.quantity || 0),
          production_date: l.production_date || null,
        }));
        // Alimente aussi stockMap pour les lots référencés dans le BL
        usable.forEach((l) => {
          if (currentLotIds.has(l.id)) {
            stockMap[l.id] = {
              available: Number(l.quantity || 0),
              lot_number: l.lot_number || "",
              product_name: l.product_name || "",
            };
          }
        });
      });
      setProductLots(pLots);
      setLotStock(stockMap);
    } catch {
      setLotStock({});
      setProductLots({});
    }

    // Phase 101ct (Feb 2026) — Charge la liste des produits + statut BL libre.
    // Ces données servent au dialogue d'ajout de ligne (visible seulement si free).
    try {
      const [linkedResp, prodResp] = await Promise.all([
        api.get(`/delivery-notes/${id}/linked-order`),
        api.get("/products"),
      ]);
      setIsFreeBl(!linkedResp.data?.linked);
      setLinkedCoRef(linkedResp.data?.co_reference || "");
      setAllProducts((prodResp.data || []).filter((p) => !p.archived));
    } catch (e) {
      // Défaut prudent : on interdit l'ajout si l'appel échoue
      setIsFreeBl(false);
      setLinkedCoRef("");
      setAllProducts([]);
    }
  };
  const cancelEditLines = () => {
    setEditLinesMode(false);
    setDraftLines([]);
    setProductLots({});
    setAddLineOpen(false);
    setAddForm({ product_id: "", lot_id: "", quantity: 1, unit_price: "" });
    setAddFormLots([]);
  };

  // Phase 101ct — Charge les lots disponibles pour un produit au dépôt source.
  // Appelé quand l'utilisateur change de produit dans le dialogue d'ajout.
  const loadAddFormLotsForProduct = async (productId) => {
    if (!productId) { setAddFormLots([]); return; }
    setAddFormLotsLoading(true);
    try {
      const r = await api.get(`/lots?product_id=${productId}`);
      const sourceId = bl?.from_distributor_id;
      const lots = (r.data || [])
        .filter((l) => Number(l.quantity || 0) > 0)
        .filter((l) => !sourceId || l.distributor_id === sourceId)
        .sort((a, b) => (a.dlc || "9999-99-99").localeCompare(b.dlc || "9999-99-99"));
      setAddFormLots(lots);
    } catch {
      setAddFormLots([]);
    } finally {
      setAddFormLotsLoading(false);
    }
  };

  const openAddLine = () => {
    setAddForm({ product_id: "", lot_id: "", quantity: 1, unit_price: "" });
    setAddFormLots([]);
    setAddLineOpen(true);
  };

  const confirmAddLine = () => {
    const prod = allProducts.find((p) => p.id === addForm.product_id);
    const lot = addFormLots.find((l) => l.id === addForm.lot_id);
    const qty = Number(addForm.quantity);
    if (!prod) { toast.error("Sélectionnez un produit"); return; }
    if (!lot) { toast.error("Sélectionnez un lot"); return; }
    if (!qty || qty <= 0) { toast.error("Quantité invalide"); return; }
    if (qty > Number(lot.quantity || 0) + 0.001) {
      toast.error(`Stock insuffisant sur le lot ${lot.lot_number || "?"} : ${lot.quantity} disponible.`);
      return;
    }
    const price = addForm.unit_price === "" ? null : Number(addForm.unit_price);
    const newLine = {
      _key: `new-${Date.now()}-${Math.random()}`,
      lot_id: lot.id,
      product_id: prod.id,
      product_name: prod.name || "",
      product_sku: prod.sku || "",
      lot_number: lot.lot_number || "",
      quantity: qty,
      dlc: lot.dlc || null,
      production_date: lot.production_date || null,
      unit_price: price,
      unit: prod.unit || "pcs",
      notes: "",
      _deleted: false,
      _isNew: true,
    };
    setDraftLines((prev) => [...prev, newLine]);
    // Alimente productLots + lotStock pour que les autres widgets fonctionnent
    setProductLots((prev) => {
      if (prev[prod.id]) return prev;
      return {
        ...prev,
        [prod.id]: addFormLots.map((l) => ({
          id: l.id,
          lot_number: l.lot_number || "",
          dlc: l.dlc || null,
          quantity: Number(l.quantity || 0),
          production_date: l.production_date || null,
        })),
      };
    });
    setLotStock((prev) => ({
      ...prev,
      [lot.id]: {
        available: Number(lot.quantity || 0),
        lot_number: lot.lot_number || "",
        product_name: prod.name || "",
      },
    }));
    setAddLineOpen(false);
    toast.success(`Ligne ajoutée : ${prod.name} × ${qty}`);
  };
  const updateDraftQty = (key, value) => {
    const n = Number(value);
    setDraftLines((prev) => prev.map((ln) => ln._key === key ? { ...ln, quantity: isNaN(n) ? 0 : Math.max(0, n) } : ln));
  };
  // Phase 98am — Change le lot d'une ligne (override du FIFO auto).
  // Utile pour livrer à un client qui souhaite une DLC homogène : on force
  // toutes les lignes d'un même produit sur le même lot manuellement.
  const changeDraftLot = (key, newLotId) => {
    setDraftLines((prev) => prev.map((ln) => {
      if (ln._key !== key) return ln;
      const lot = (productLots[ln.product_id] || []).find((l) => l.id === newLotId);
      if (!lot) return ln;
      return {
        ...ln,
        lot_id: lot.id,
        lot_number: lot.lot_number,
        dlc: lot.dlc,
        production_date: lot.production_date,
      };
    }));
    // Alimente lotStock avec l'available du lot nouvellement sélectionné (pour l'overflow check).
    setLotStock((prev) => {
      const lot = Object.values(productLots).flat().find((l) => l.id === newLotId);
      if (!lot) return prev;
      return { ...prev, [newLotId]: { available: lot.quantity, lot_number: lot.lot_number, product_name: "" } };
    });
  };
  // Phase 98ah — Édition du PU par ligne (utile pour ajuster une facture revendeur).
  // Applique la nouvelle valeur à toutes les lignes du MÊME produit (par défaut) pour
  // éviter des incohérences entre lots HACCP d'un même article. Un shift+clic permettra
  // (futur) d'éditer uniquement le lot ciblé. Pour l'instant : toujours par produit.
  const updateDraftPrice = (key, value, applyAllSameProduct = true) => {
    const n = Number(value);
    const clean = isNaN(n) ? null : Math.max(0, n);
    setDraftLines((prev) => {
      const target = prev.find((l) => l._key === key);
      if (!target) return prev;
      return prev.map((ln) => {
        if (applyAllSameProduct && ln.product_id === target.product_id && !ln._deleted) {
          return { ...ln, unit_price: clean };
        }
        if (ln._key === key) return { ...ln, unit_price: clean };
        return ln;
      });
    });
  };
  const toggleDeleteLine = (key) => {
    setDraftLines((prev) => prev.map((ln) => ln._key === key ? { ...ln, _deleted: !ln._deleted } : ln));
  };
  const saveEditedLines = async () => {
    // Ne conserve que les lignes non supprimées et avec quantité > 0
    const kept = draftLines.filter((ln) => !ln._deleted && Number(ln.quantity) > 0);
    if (kept.length === 0) {
      toast.error("Au moins une ligne doit rester (sinon annulez le BL entièrement).");
      return;
    }
    // Phase 98ai — Bloque explicitement si stock insuffisant dans un lot
    if (hasStockOverflow) {
      const first = Object.values(overflowByLot)[0];
      toast.error(
        `Stock insuffisant dans le lot ${first.lot_number} : ${first.requested} demandé pour ${first.available} disponible. Fabriquez un OF ou réduisez la quantité.`
      );
      return;
    }
    // Détecte s'il y a un vrai changement (qty, PU ou lot — Phase 98am)
    const original = bl.lines || [];
    const hasChange = kept.length !== original.length || kept.some((ln) => {
      const orig = original.find((o) => (o.id === ln._key) || (o.lot_id === ln.lot_id && o.product_id === ln.product_id));
      if (!orig) return true;
      if (Math.abs(Number(orig.quantity) - Number(ln.quantity)) > 0.001) return true;
      // Phase 98am — Comparaison des lots : un changement de lot doit déclencher un save
      if ((orig.lot_id || "") !== (ln.lot_id || "")) return true;
      const origPrice = orig.unit_price == null ? null : Number(orig.unit_price);
      const newPrice = ln.unit_price == null ? null : Number(ln.unit_price);
      if (origPrice == null && newPrice == null) return false;
      if (origPrice == null || newPrice == null) return true;
      return Math.abs(origPrice - newPrice) > 0.001;
    });
    if (!hasChange) { setEditLinesMode(false); return; }

    // Détecte si des PU ont été touchés pour un message plus explicite
    const priceChanged = kept.some((ln) => {
      const orig = original.find((o) => (o.id === ln._key) || (o.lot_id === ln.lot_id && o.product_id === ln.product_id));
      if (!orig) return false;
      const origPrice = orig.unit_price == null ? null : Number(orig.unit_price);
      const newPrice = ln.unit_price == null ? null : Number(ln.unit_price);
      if (origPrice == null && newPrice == null) return false;
      if (origPrice == null || newPrice == null) return true;
      return Math.abs(origPrice - newPrice) > 0.001;
    });

    // Phase 98am — Détecte si des lots ont été changés pour un message explicite
    const lotChanged = kept.some((ln) => {
      const orig = original.find((o) => o.id === ln._key);
      if (!orig) return false;
      return (orig.lot_id || "") !== (ln.lot_id || "");
    });

    const ok = await confirm({
      title: "Enregistrer les modifications du BL ?",
      description: (() => {
        if (priceChanged && lotChanged) {
          return "Quantités, prix unitaires ET lots seront enregistrés. La commande parente (CMD) sera réalignée sur les nouvelles quantités et prix. Les mouvements de stock utiliseront les lots que vous avez choisis (override du FIFO auto).";
        }
        if (lotChanged) {
          return "Les lots que vous avez choisis remplaceront les lots auto-sélectionnés en FIFO. Utile pour livrer un client avec une DLC homogène. Les quantités restent identiques.";
        }
        if (priceChanged) {
          return "Les quantités ET les prix unitaires seront répercutés sur la commande parente (CMD). La future facture reflètera ces nouvelles valeurs. Les unités retirées restent en stock (aucun mouvement).";
        }
        return "Les quantités de la commande parente (CMD) seront automatiquement réalignées sur ces nouvelles valeurs. Les unités retirées resteront en stock (aucun mouvement de stock).";
      })(),
      confirmText: "Enregistrer",
      cancelText: "Annuler",
    });
    if (!ok) return;

    setSavingLines(true);
    try {
      const payload = {
        lines: kept.map((ln) => ({
          lot_id: ln.lot_id,
          product_id: ln.product_id,
          quantity: Number(ln.quantity),
          unit_price: ln.unit_price,
          notes: ln.notes || "",
        })),
      };
      await api.put(`/delivery-notes/${id}`, payload);
      toast.success(
        lotChanged && !priceChanged
          ? "Lots enregistrés · nouveaux lots appliqués sur les lignes"
          : "BL modifié · CMD parente réalignée automatiquement"
      );
      setEditLinesMode(false);
      setDraftLines([]);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSavingLines(false);
    }
  };
  const draftTotalQty = useMemo(() => draftLines.filter((ln) => !ln._deleted).reduce((acc, ln) => acc + (Number(ln.quantity) || 0), 0), [draftLines]);

  // Phase 98ai — Détecte les dépassements de stock par lot (somme des qty demandées vs lot.quantity)
  const overflowByLot = useMemo(() => {
    const requested = {};
    draftLines.filter((ln) => !ln._deleted && ln.lot_id).forEach((ln) => {
      requested[ln.lot_id] = (requested[ln.lot_id] || 0) + (Number(ln.quantity) || 0);
    });
    const overflows = {};
    Object.keys(requested).forEach((lot_id) => {
      const info = lotStock[lot_id];
      if (info && requested[lot_id] > Number(info.available) + 0.001) {
        overflows[lot_id] = {
          requested: requested[lot_id],
          available: Number(info.available),
          lot_number: info.lot_number,
          product_name: info.product_name,
        };
      }
    });
    return overflows;
  }, [draftLines, lotStock]);
  const hasStockOverflow = Object.keys(overflowByLot).length > 0;

  // Édition rapide de la date de livraison
  const [editingDate, setEditingDate] = useState(false);
  const [dateDraft, setDateDraft] = useState("");
  const [savingDate, setSavingDate] = useState(false);
  // Phase 101bx (18/02/2026) — Bugfix timezone : on utilise les helpers Zurich
  // au lieu du toLocalInput naïf basé sur browser TZ. Sinon le picker affichait
  // -1h/+1h vs le PDF selon la saison (CET/CEST) et la modification saisie ne
  // correspondait pas à celle imprimée sur le BL/facture.
  const startEditDate = () => { setDateDraft(isoToLocalInput(bl.delivery_date)); setEditingDate(true); };
  const saveDate = async () => {
    if (!dateDraft) { toast.error("Date invalide"); return; }
    setSavingDate(true);
    try {
      const iso = localInputToIso(dateDraft);
      if (!iso) { toast.error("Date invalide"); setSavingDate(false); return; }
      await api.put(`/delivery-notes/${id}`, { delivery_date: iso });
      toast.success("Date de livraison mise à jour · facture liée synchronisée");
      setEditingDate(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSavingDate(false);
    }
  };

  // Phase 101by (18/02/2026) — Génère (ou ouvre) la facture BROUILLON liée au BL.
  // Cas d'usage utilisatrice : « d'un simple BL je veux pouvoir faire une facture ».
  // Idempotent — si une facture existe déjà pour la CMD liée, l'endpoint la renvoie
  // au lieu de créer un doublon.
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const generateInvoice = async () => {
    if (generatingInvoice) return;
    setGeneratingInvoice(true);
    try {
      const { data } = await api.post(`/delivery-notes/${id}/generate-invoice`);
      if (data && data.id) {
        toast.success(`Facture ${data.reference || ""} prête · ouverture`);
        navigate(`/factures/${data.id}`);
      } else {
        toast.error("Réponse inattendue de l'API facture");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la génération de la facture");
    } finally {
      setGeneratingInvoice(false);
    }
  };

  // Phase 101fr — Attache un revendeur rétroactivement + recalcule prix négociés
  const openAttachDialog = () => {
    setAttachForm({ reseller_id: "", apply_prices: true, switch_distributor: false });
    setAttachDialogOpen(true);
  };
  const submitAttachReseller = async () => {
    if (!attachForm.reseller_id) {
      toast.error("Sélectionne un revendeur d'abord.");
      return;
    }
    setAttaching(true);
    try {
      const { data } = await api.post(`/delivery-notes/${id}/attach-reseller`, {
        reseller_id: attachForm.reseller_id,
        apply_negotiated_prices: attachForm.apply_prices,
        switch_distributor: attachForm.switch_distributor,
      });
      const parts = [`Revendeur ${data.reseller_name} attaché`];
      if (data.price_changes_count > 0) parts.push(`${data.price_changes_count} prix recalculé(s)`);
      if (data.client_order_updated) parts.push("CMD liée synchronisée");
      if (data.distributor_switched) parts.push("destinataire mis à jour");
      toast.success(parts.join(" · "));
      setAttachDialogOpen(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur attach revendeur");
    } finally {
      setAttaching(false);
    }
  };
  const recomputeResellerPrices = async () => {
    try {
      const { data } = await api.post(`/delivery-notes/${id}/recompute-reseller-prices`);
      if (data.changes_count === 0) {
        toast.info("Prix déjà à jour");
      } else {
        toast.success(`${data.changes_count} prix recalculé(s) depuis ${data.reseller_name}`);
      }
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur recalcul");
    }
  };

  if (loadError) {
    return (
      <div className="max-w-2xl mx-auto mt-10 bg-white border-2 border-[#C05C47] rounded-lg p-6" data-testid="bl-load-error">
        <h2 className="text-lg font-semibold text-[#C05C47] mb-2">Impossible d&apos;ouvrir le bon de livraison</h2>
        <p className="text-sm text-[#1F2624] mb-4">{loadError}</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => reload()}>Réessayer</Button>
          <Button variant="ghost" onClick={() => navigate("/bons-livraison")}>Retour à la liste</Button>
        </div>
      </div>
    );
  }
  if (!bl) return <div className="text-[#575A57]" data-testid="bl-loading">Chargement…</div>;

  const meta = STATUS_META[bl.status] || STATUS_META.BROUILLON;

  return (
    <div className="space-y-6" data-testid="bl-detail-page">
      <div className="flex items-center gap-4 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => navigate("/bons-livraison")} data-testid="back-to-bls">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-heading text-[#1A1C1A]">{bl.reference}</h1>
            <Badge className={meta.className}>{meta.label}</Badge>
            {bl.transfer_executed && <Badge variant="outline" className="text-[10px]"><ClipboardCheck className="w-3 h-3 mr-1" />Transferts exécutés</Badge>}
            {bl.corrected_at && (
              <Badge variant="outline" className="text-[10px] bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]" title={`Corrigé le ${fmtDateTime(bl.corrected_at)}${bl.correction_notes ? " — " + bl.correction_notes : ""}`}>
                <Undo2 className="w-3 h-3 mr-1" />Corrigé · {(bl.correction_movement_ids || []).length} mvt
              </Badge>
            )}
          </div>
          <div className="text-sm text-[#575A57] flex items-center gap-2 flex-wrap">
            <span>Pour {bl.distributor_name}
              {(() => {
                const destDist = distributors.find((d) => d.id === bl.distributor_id);
                if (destDist?.is_laboratory) {
                  return (
                    <Badge
                      variant="outline"
                      className="bg-[#E7F0F5] border-[#95B8C9] text-[#164D63] text-[10px] gap-1 ml-2"
                      data-testid="bl-badge-laboratory"
                    >
                      🔬 Laboratoire HACCP
                    </Badge>
                  );
                }
                return null;
              })()}
              ·</span>
            {!editingDate ? (
              <>
                <span>livraison du <strong>{fmtDate(bl.delivery_date)}</strong></span>
                {!["SIGNE", "LIVRE", "ANNULE"].includes(bl.status) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-[10px] text-[#8C6B2F] hover:bg-[#FBF4E9]"
                    onClick={startEditDate}
                    data-testid="bl-edit-date-btn"
                    title="Modifier la date de livraison"
                  >
                    <CalendarClock className="w-3 h-3 mr-1" /> modifier
                  </Button>
                )}
              </>
            ) : (
              <span className="inline-flex items-center gap-1">
                <Input
                  type="datetime-local"
                  value={dateDraft}
                  onChange={(e) => setDateDraft(e.target.value)}
                  className="h-7 text-xs w-[180px]"
                  data-testid="bl-edit-date-input"
                />
                <Button size="sm" className="h-7 bg-[#1F4E3D] hover:bg-[#173B2E]" onClick={saveDate} disabled={savingDate} data-testid="bl-edit-date-save">
                  {savingDate ? "…" : "OK"}
                </Button>
                <Button size="sm" variant="ghost" className="h-7" onClick={() => setEditingDate(false)} data-testid="bl-edit-date-cancel">
                  <X className="w-3.5 h-3.5" />
                </Button>
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="bl-download-pdf">
                <Download className="w-4 h-4 mr-2" /> Aperçu / PDF <ChevronDown className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuItem onClick={() => downloadPdf(true)} data-testid="bl-pdf-with-prices">
                <Banknote className="w-4 h-4 mr-2 text-[#1F4E3D]" /> BL avec prix (interne)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadPdf(false)} data-testid="bl-pdf-without-prices">
                <EyeOff className="w-4 h-4 mr-2 text-[#8C6B2F]" /> BL sans prix (client)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {!bl.transfer_executed && bl.from_distributor_id && (
            <Button
              onClick={executeTransfers}
              className="bg-[#1F4E3D] hover:bg-[#173B2E] disabled:opacity-50"
              disabled={hasLineWithoutLot || (bl.lines || []).length === 0}
              title={hasLineWithoutLot ? "Au moins une ligne n'a pas de lot source — impossible d'exécuter la sortie de stock" : ""}
              data-testid="bl-execute-transfer"
            >
              <ClipboardCheck className="w-4 h-4 mr-2" />
              {isOutDelivery ? "Confirmer la sortie de stock" : "Exécuter les transferts"}
            </Button>
          )}
        </div>
      </div>

      {/* Status workflow */}
      <Card className="border-[#E5E5E0]">
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <span className="text-xs text-[#575A57] uppercase tracking-wide">Workflow</span>
          {bl.status === "BROUILLON" && (
            <Button size="sm" variant="outline" onClick={() => updateStatus("ENVOYE")} data-testid="bl-status-envoye">
              <Send className="w-3.5 h-3.5 mr-1.5" /> Marquer envoyé
            </Button>
          )}
          {(bl.status === "BROUILLON" || bl.status === "ENVOYE") && (
            <Button size="sm" variant="outline" onClick={() => updateStatus("LIVRE")} data-testid="bl-status-livre">
              <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" /> Marquer livré
            </Button>
          )}
          {bl.status !== "SIGNE" && bl.status !== "ANNULE" && (
            <Button size="sm" variant="outline" onClick={() => setSignOpen(true)} data-testid="bl-status-signe">
              <PenLine className="w-3.5 h-3.5 mr-1.5" /> Marquer signé
            </Button>
          )}
          {bl.status !== "ANNULE" && (
            <Button size="sm" variant="ghost" className="text-[#C05C47]" onClick={() => updateStatus("ANNULE")} data-testid="bl-status-annule">
              Annuler le BL
            </Button>
          )}
          {/* Phase 101ea — Restaurer un BL annulé par erreur (aucun effet stock). */}
          {bl.status === "ANNULE" && (
            <Button
              size="sm"
              variant="outline"
              className="border-[#1F4E3D] text-[#1F4E3D] hover:bg-[#EAF4EE]"
              onClick={restoreCancelled}
              data-testid="bl-restore-btn"
              title="Restaure ce BL à son statut d'avant annulation (aucun effet stock)"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Restaurer
            </Button>
          )}
          {/* Phase 101by (18/02/2026) — Bouton « Générer facture » directement depuis le BL.
              Idempotent : si une facture existe déjà (via CMD liée), ouvre-la. Sinon crée
              une facture BROUILLON avec les lignes du BL (regroupées par produit). */}
          {bl.status !== "ANNULE" && (
            <Button
              size="sm"
              variant="outline"
              className="border-[#1F4E3D] text-[#1F4E3D] hover:bg-[#EAF4EE]"
              onClick={generateInvoice}
              disabled={generatingInvoice || (bl.lines || []).length === 0}
              data-testid="bl-generate-invoice-btn"
              title="Génère (ou ouvre) la facture BROUILLON correspondant à ce BL"
            >
              <Receipt className="w-3.5 h-3.5 mr-1.5" /> {generatingInvoice ? "…" : "Générer facture"}
            </Button>
          )}
          {/* Phase 101fr — Attacher/rappliquer prix revendeur */}
          {bl.status !== "ANNULE" && !bl.reseller_id && (
            <Button
              size="sm"
              variant="outline"
              className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBF4E9]"
              onClick={openAttachDialog}
              data-testid="bl-attach-reseller-btn"
              title="Attache un revendeur à ce BL libre + applique les prix négociés"
            >
              <Banknote className="w-3.5 h-3.5 mr-1.5" /> Attacher revendeur
            </Button>
          )}
          {bl.status !== "ANNULE" && bl.reseller_id && !bl.transfer_executed && (
            <Button
              size="sm"
              variant="outline"
              className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBF4E9]"
              onClick={recomputeResellerPrices}
              data-testid="bl-recompute-prices-btn"
              title="Recalcule les prix depuis les négociations actuelles du revendeur"
            >
              <Banknote className="w-3.5 h-3.5 mr-1.5" /> Recalculer prix
            </Button>
          )}
          {bl.transfer_executed && ["ENVOYE", "LIVRE", "SIGNE"].includes(bl.status) && (
            <DeliveryCorrectionDialog
              bl={bl}
              distributors={distributors}
              onDone={load}
              trigger={
                <Button size="sm" variant="outline" className="border-[#8C6B2F]/40 text-[#8C6B2F] hover:bg-[#FBF4E9]" data-testid="bl-correct-btn">
                  <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Corriger la livraison
                </Button>
              }
            />
          )}
          {!bl.transfer_executed && bl.status !== "ANNULE" && !editLinesMode && (
            <Button
              size="sm"
              variant="outline"
              className="border-[#1F4E3D]/40 text-[#1F4E3D] hover:bg-[#EAF4EE]"
              onClick={startEditLines}
              disabled={(bl.lines || []).length === 0}
              data-testid="bl-edit-lines-btn"
            >
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> Modifier les lignes
            </Button>
          )}
          {!bl.transfer_executed && (
            <Button size="sm" variant="ghost" className="text-[#C05C47] ml-auto" onClick={remove} data-testid="bl-delete">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Supprimer
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-[#E5E5E0]">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-[#A3A6A3]">Destinataire</div>
            {/* Format suisse — Société / Contact / Rue / NPA Localité */}
            <div className="font-medium text-[#1A1C1A] mt-1"><AccountLink resellerId={bl.reseller_id} distributorId={bl.distributor_id} testid="bl-detail-account">{bl.distributor_name}</AccountLink></div>
            {bl.distributor_contact && <div className="text-xs text-[#575A57]">{bl.distributor_contact}</div>}
            {addressLines(bl.distributor_address).map((line, i) => (
              <div key={i} className="text-xs text-[#575A57]">{line}</div>
            ))}
          </CardContent>
        </Card>

        {/* Phase 101fa — Tests labo auto-générés depuis ce BL */}
        {(bl.generated_lab_test_ids || []).length > 0 && (
          <Card className="border-[#95B8C9] bg-[#F3F8FA] md:col-span-3">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wide text-[#164D63] flex items-center gap-2">
                  🔬 Tests HACCP générés automatiquement
                  <Badge variant="outline" className="bg-white border-[#95B8C9] text-[#164D63] text-[10px]">
                    {bl.generated_lab_test_ids.length} test{bl.generated_lab_test_ids.length > 1 ? "s" : ""}
                  </Badge>
                </div>
                <Link
                  to={`/tests-labo?bl_ref=${encodeURIComponent(bl.reference)}`}
                  className="text-xs text-[#164D63] underline hover:opacity-80"
                  data-testid="bl-view-lab-tests-link"
                >
                  Voir tous les tests →
                </Link>
              </div>
              <p className="text-[11px] text-[#575A57]">
                Un test HACCP <strong>« en attente »</strong> a été créé pour chaque ligne + lot de ce BL, marqué
                <code className="mx-1 px-1 bg-white rounded text-[10px]">[BL {bl.reference}]</code> dans les notes.
                Quand tu recevras les résultats d&apos;ARQHA, ouvre chaque test pour renseigner
                <strong> la date d&apos;analyse, le résultat et joindre le PDF</strong>.
              </p>
            </CardContent>
          </Card>
        )}

        <Card className="border-[#E5E5E0]">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-[#A3A6A3]">Dépôt source</div>
            <div className="font-medium text-[#1A1C1A] mt-1">{bl.from_distributor_name || "—"}</div>
            {!bl.from_distributor_id && (
              <div className="text-xs text-[#8C6B2F] flex items-center gap-1 mt-1">
                <AlertTriangle className="w-3 h-3" /> Pas de source — transferts non exécutables
              </div>
            )}
            {bl.from_distributor_id && hasLineWithoutLot && !bl.transfer_executed && (
              <div className="text-xs text-[#8C6B2F] flex items-center gap-1 mt-1">
                <AlertTriangle className="w-3 h-3" /> Au moins une ligne sans lot — transferts non exécutables
              </div>
            )}
          </CardContent>
        </Card>
        <Card className="border-[#E5E5E0]">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-[#A3A6A3]">Total</div>
            <div className="font-mono text-2xl text-[#1A1C1A] mt-1">{fmtNum(totalQty)} <span className="text-sm font-normal text-[#575A57]">unités · {groupedLines.length} ligne{groupedLines.length > 1 ? "s" : ""}</span></div>
          </CardContent>
        </Card>
      </div>

      {/* Lines */}
      <Card className="border-[#E5E5E0]">
        <CardContent className="p-0">
          <div className="px-5 py-3 border-b border-[#E5E5E0] flex items-center gap-2 justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#1F4E3D]" />
              <span className="text-sm font-medium text-[#1A1C1A]">
                {editLinesMode ? "Édition des lignes (une ligne par lot HACCP)" : "Lignes du bon de livraison"}
              </span>
              <span className="text-xs text-[#A3A6A3]">
                {editLinesMode
                  ? `(${draftLines.filter((ln) => !ln._deleted).length} ligne(s) conservée(s) · ${fmtNum(draftTotalQty)} unité(s))`
                  : `(${groupedLines.length} ligne${groupedLines.length > 1 ? "s" : ""} consolidée${groupedLines.length > 1 ? "s" : ""} · ${fmtNum(totalQty)} unité${totalQty > 1 ? "s" : ""})`}
              </span>
            </div>
            {!editLinesMode && (bl.lines || []).length > groupedLines.length && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowHaccpDetail((v) => !v)}
                className="text-xs text-[#8C6B2F]"
                data-testid="toggle-haccp-detail"
              >
                {showHaccpDetail ? "Masquer" : "Voir"} le détail HACCP par lot ({(bl.lines || []).length} ligne{(bl.lines || []).length > 1 ? "s" : ""} brute{(bl.lines || []).length > 1 ? "s" : ""})
              </Button>
            )}
          </div>
          {editLinesMode && (
            <div className="px-5 py-3 bg-[#E5EFF8] border-b border-[#C5D8E8] flex items-start gap-2" data-testid="bl-edit-info-banner">
              <Info className="w-4 h-4 text-[#2D5A87] shrink-0 mt-0.5" />
              <div className="text-xs text-[#2D5A87] leading-relaxed">
                <div className="font-medium">Réalignement automatique de la CMD parente</div>
                <div>Vous pouvez ajuster les <b>quantités</b>, les <b>prix unitaires</b> (utile pour les tarifs revendeur négociés au coup par coup) et <b>choisir un autre lot</b> (utile quand un client veut une DLC homogène — override du FIFO auto). La commande parente sera automatiquement mise à jour pour que la facture générée soit correcte. Les unités retirées <b>restent en stock au dépôt source</b> (aucun mouvement de stock — le transfert n&apos;est pas encore exécuté).</div>
                <div className="mt-1 text-[11px] italic">Astuce : modifier un PU l&apos;applique à toutes les lignes du même produit sur ce BL. Le sélecteur de lot est indépendant par ligne.</div>
              </div>
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Produit</TableHead>
                <TableHead>SKU</TableHead>
                {editLinesMode && <TableHead>Lot HACCP</TableHead>}
                <TableHead className="text-right">Quantité</TableHead>
                <TableHead>DLC</TableHead>
                <TableHead className="text-right">PU CHF</TableHead>
                {editLinesMode && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {!editLinesMode && groupedLines.map((ln, idx) => (
                <TableRow key={`grp-${idx}`} data-testid={`bl-line-${idx}`}>
                  <TableCell>
                    <div>{ln.product_name || "—"}</div>
                    {ln.source_lots.length > 1 && (
                      <div className="text-[10px] text-[#A3A6A3] mt-0.5">
                        Issu de {ln.source_lots.length} lots
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-[#575A57]">{ln.product_sku || "—"}</TableCell>
                  <TableCell className="text-right font-mono">{fmtNum(ln.quantity, ln.unit)}</TableCell>
                  <TableCell className="text-xs">{ln.dlc ? fmtDate(ln.dlc) : "—"}</TableCell>
                  <TableCell className="text-right font-mono">{ln.unit_price ? Number(ln.unit_price).toFixed(2) : "—"}</TableCell>
                </TableRow>
              ))}
              {editLinesMode && draftLines.map((ln, idx) => {
                const isOver = ln.lot_id && overflowByLot[ln.lot_id] && !ln._deleted;
                const lotInfo = ln.lot_id ? lotStock[ln.lot_id] : null;
                const maxStock = lotInfo ? Number(lotInfo.available) : null;
                return (
                <TableRow
                  key={ln._key}
                  data-testid={`bl-edit-row-${idx}`}
                  className={ln._deleted ? "bg-[#FCEEEA] opacity-60" : (isOver ? "bg-[#FCEEEA]" : "")}
                >
                  <TableCell>
                    <div className={ln._deleted ? "line-through text-[#C05C47]" : ""}>{ln.product_name || "—"}</div>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-[#575A57]">{ln.product_sku || "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-[#8C6B2F]">
                    {(() => {
                      const lots = productLots[ln.product_id] || [];
                      // Fallback : si aucun choix possible (pas d'autre lot), affiche read-only
                      if (lots.length <= 1 || ln._deleted) {
                        return ln.lot_number || "—";
                      }
                      return (
                        <Select
                          value={ln.lot_id || ""}
                          onValueChange={(v) => changeDraftLot(ln._key, v)}
                        >
                          <SelectTrigger
                            className="h-8 w-auto min-w-[140px] max-w-[220px] font-mono text-xs"
                            data-testid={`bl-edit-lot-select-${idx}`}
                            title="Choisir manuellement le lot. Par défaut FIFO (DLC la plus courte)."
                          >
                            <SelectValue>
                              {ln.lot_number || "—"}
                              {ln.dlc && <span className="text-[10px] text-[#575A57] ml-1">· {fmtDate(ln.dlc)}</span>}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {lots.map((l) => (
                              <SelectItem
                                key={l.id}
                                value={l.id}
                                data-testid={`bl-edit-lot-option-${idx}-${l.lot_number}`}
                              >
                                <span className="font-mono">{l.lot_number || "(sans n°)"}</span>
                                {l.dlc && (
                                  <span className="text-[10px] text-[#575A57] ml-2">
                                    DLC {fmtDate(l.dlc)}
                                  </span>
                                )}
                                <span className="text-[10px] text-[#849F90] ml-2">
                                  · {l.quantity} dispo
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        className={`h-8 w-20 text-right font-mono ${isOver ? "border-[#C05C47] bg-white" : ""}`}
                        value={ln.quantity}
                        disabled={ln._deleted}
                        onChange={(e) => updateDraftQty(ln._key, e.target.value)}
                        data-testid={`bl-edit-qty-${idx}`}
                      />
                      {maxStock != null && !ln._deleted && (
                        <span
                          className={`text-[10px] font-mono whitespace-nowrap ${isOver ? "text-[#C05C47] font-semibold" : "text-[#A3A6A3]"}`}
                          title={`Stock physique du lot : ${maxStock} unité(s). Impossible de dépasser ce nombre sans fabriquer un OF supplémentaire.`}
                          data-testid={`bl-edit-max-${idx}`}
                        >
                          {isOver ? "⚠" : ""} /{maxStock}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">{ln.dlc ? fmtDate(ln.dlc) : "—"}</TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="number"
                      min="0"
                      step="0.05"
                      placeholder="—"
                      className="h-8 w-24 text-right font-mono ml-auto"
                      value={ln.unit_price ?? ""}
                      disabled={ln._deleted}
                      onChange={(e) => updateDraftPrice(ln._key, e.target.value)}
                      title="Prix unitaire CHF. Modifier ici s'applique à tous les lots du même produit et sera propagé à la CMD parente pour la facture."
                      data-testid={`bl-edit-price-${idx}`}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      className={ln._deleted ? "text-[#1F4E3D]" : "text-[#C05C47]"}
                      onClick={() => toggleDeleteLine(ln._key)}
                      data-testid={`bl-edit-delete-${idx}`}
                    >
                      {ln._deleted ? (
                        <><Undo2 className="w-3.5 h-3.5 mr-1" /> Rétablir</>
                      ) : (
                        <><Trash2 className="w-3.5 h-3.5 mr-1" /> Retirer</>
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {editLinesMode && (
            <div className="px-5 py-3 border-t border-[#E5E5E0] flex items-center justify-between gap-3 bg-[#FAFAF7] flex-wrap">
              {hasStockOverflow ? (
                <div className="text-xs text-[#C05C47] flex-1 min-w-0" data-testid="bl-edit-stock-warning">
                  <b>⚠ Impossible d&apos;enregistrer :</b>
                  {Object.entries(overflowByLot).slice(0, 2).map(([lotId, o]) => (
                    <span key={lotId} className="block">
                      Lot <span className="font-mono">{o.lot_number}</span> ({o.product_name}) — demandé <b>{o.requested}</b> mais seulement <b>{o.available}</b> en stock. Fabriquez un OF ou réduisez la quantité.
                    </span>
                  ))}
                </div>
              ) : (
                /* Phase 101fe (Feb 2026) — Ajout de ligne autorisé sur tout BL non-exécuté,
                   même lié à une CMD parente. Le backend étend automatiquement la CMD
                   avec les nouveaux `product_id` du BL (voir `_realign_client_order_from_bl`). */
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={openAddLine}
                    disabled={savingLines}
                    className="border-[#1F4E3D] text-[#1F4E3D] hover:bg-[#EAF4EE]"
                    data-testid="bl-edit-add-line"
                    title={
                      isFreeBl
                        ? "Ajouter un article au BL"
                        : `Ajouter un article — la CMD parente ${linkedCoRef ? linkedCoRef : ""} sera étendue automatiquement`
                    }
                  >
                    <Pencil className="w-3.5 h-3.5 mr-1.5" /> Ajouter une ligne
                  </Button>
                  {!isFreeBl && linkedCoRef && (
                    <span className="text-[11px] text-[#575A57] italic" data-testid="bl-edit-add-line-info">
                      La CMD <span className="font-mono font-semibold">{linkedCoRef}</span> sera étendue automatiquement.
                    </span>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={cancelEditLines}
                  disabled={savingLines}
                  data-testid="bl-edit-cancel"
                >
                  <X className="w-3.5 h-3.5 mr-1.5" /> Annuler
                </Button>
                <Button
                  size="sm"
                  className="bg-[#1F4E3D] hover:bg-[#173B2E]"
                  onClick={saveEditedLines}
                  disabled={savingLines || hasStockOverflow}
                  data-testid="bl-edit-save"
                >
                  <Save className="w-3.5 h-3.5 mr-1.5" /> {savingLines ? "Enregistrement…" : "Enregistrer les modifications"}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Phase 101ct (Feb 2026) — Dialog d'ajout d'une ligne (BL libre uniquement).
          Produit + lot manuel + quantité + PU. Le lot est filtré au dépôt source du BL. */}
      <Dialog open={addLineOpen} onOpenChange={setAddLineOpen}>
        <DialogContent className="max-w-lg" data-testid="bl-add-line-dialog">
          <DialogHeader>
            <DialogTitle>Ajouter un article au BL</DialogTitle>
            <DialogDescription>
              Choisissez le produit puis le lot HACCP à prélever du dépôt source
              {bl?.from_distributor_name ? <> ({bl.from_distributor_name})</> : null}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-[#575A57]">Produit</Label>
              <Select
                value={addForm.product_id}
                onValueChange={(v) => {
                  setAddForm((f) => ({ ...f, product_id: v, lot_id: "" }));
                  loadAddFormLotsForProduct(v);
                }}
              >
                <SelectTrigger data-testid="bl-add-line-product">
                  <SelectValue placeholder="Choisir un produit" />
                </SelectTrigger>
                <SelectContent>
                  {[...allProducts]
                    .sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" }))
                    .map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}{p.sku ? ` (${p.sku})` : ""}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#575A57]">
                Lot HACCP <span className="text-[#C05C47]">*</span>
              </Label>
              <Select
                value={addForm.lot_id}
                onValueChange={(v) => setAddForm((f) => ({ ...f, lot_id: v }))}
                disabled={!addForm.product_id || addFormLotsLoading}
              >
                <SelectTrigger data-testid="bl-add-line-lot">
                  <SelectValue placeholder={
                    !addForm.product_id ? "Choisissez d'abord un produit"
                      : addFormLotsLoading ? "Chargement…"
                      : addFormLots.length === 0 ? "Aucun lot disponible au dépôt source"
                      : "Choisir un lot"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {addFormLots.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      <span className="font-mono">{l.lot_number || "(sans n°)"}</span>
                      {l.dlc && <span className="text-[10px] text-[#575A57] ml-2">DLC {fmtDate(l.dlc)}</span>}
                      <span className="text-[10px] text-[#849F90] ml-2">· {l.quantity} dispo</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {addForm.product_id && !addFormLotsLoading && addFormLots.length === 0 && (
                <div className="text-[11px] text-[#C05C47] mt-1" data-testid="bl-add-line-no-lot">
                  Aucun lot disponible pour ce produit au dépôt source. Fabriquez un OF ou choisissez un autre article.
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#575A57]">Quantité</Label>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={addForm.quantity}
                  onChange={(e) => setAddForm((f) => ({ ...f, quantity: e.target.value }))}
                  data-testid="bl-add-line-qty"
                />
              </div>
              <div>
                <Label className="text-xs text-[#575A57]">PU CHF (facultatif)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.05"
                  placeholder="—"
                  value={addForm.unit_price}
                  onChange={(e) => setAddForm((f) => ({ ...f, unit_price: e.target.value }))}
                  data-testid="bl-add-line-price"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddLineOpen(false)} data-testid="bl-add-line-cancel">
              Annuler
            </Button>
            <Button
              className="bg-[#1F4E3D] hover:bg-[#173B2E]"
              onClick={confirmAddLine}
              disabled={!addForm.product_id || !addForm.lot_id || Number(addForm.quantity || 0) <= 0}
              data-testid="bl-add-line-confirm"
            >
              <Save className="w-3.5 h-3.5 mr-1.5" /> Ajouter la ligne
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Détail HACCP par lot (collapsible, traçabilité interne) */}
      {showHaccpDetail && (
        <Card className="border-[#E8D3A8] bg-[#FBF4E9]/30" data-testid="haccp-detail-card">
          <CardContent className="p-0">
            <div className="px-5 py-3 border-b border-[#E8D3A8] flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-[#8C6B2F]" />
              <span className="text-sm font-medium text-[#8C6B2F]">Détail traçabilité HACCP (interne)</span>
              <span className="text-xs text-[#A3A6A3]">— ne figure pas sur le PDF client</span>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lot</TableHead>
                  <TableHead>Produit</TableHead>
                  <TableHead className="text-right">Quantité</TableHead>
                  <TableHead>Date fab.</TableHead>
                  <TableHead>DLC</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(bl.lines || []).map((ln, idx) => (
                  <TableRow key={ln.id || idx} data-testid={`bl-raw-line-${idx}`}>
                    <TableCell className="font-mono text-xs">
                      {ln.lot_number ? <Link to={`/tracabilite?lot=${ln.lot_number}`} className="text-[#1F4E3D] hover:underline">{ln.lot_number}</Link> : "—"}
                    </TableCell>
                    <TableCell>{ln.product_name || "—"}</TableCell>
                    <TableCell className="text-right font-mono">{fmtNum(ln.quantity, ln.unit)}</TableCell>
                    <TableCell className="text-xs text-[#8C6B2F] font-mono">{ln.production_date ? fmtDate(ln.production_date) : "—"}</TableCell>
                    <TableCell className="text-xs">{ln.dlc ? fmtDate(ln.dlc) : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {bl.notes && (
        <Card className="border-[#E5E5E0]">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-[#A3A6A3] mb-1">Notes</div>
            <div className="text-sm text-[#1A1C1A]">{bl.notes}</div>
          </CardContent>
        </Card>
      )}

      {(bl.delivered_at || bl.signed_at) && (
        <Card className="border-[#E5E5E0]">
          <CardContent className="p-4 grid grid-cols-2 gap-4">
            {bl.delivered_at && (
              <div>
                <div className="text-xs uppercase tracking-wide text-[#A3A6A3]">Livré le</div>
                <div className="text-sm">{fmtDateTime(bl.delivered_at)}</div>
              </div>
            )}
            {bl.signed_at && (
              <div>
                <div className="text-xs uppercase tracking-wide text-[#A3A6A3]">Signé par</div>
                <div className="text-sm font-medium">{bl.signed_by || "—"}</div>
                <div className="text-xs text-[#575A57]">{fmtDateTime(bl.signed_at)}</div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Marquer le BL comme signé</DialogTitle>
            <DialogDescription>Entrez le nom du signataire client (réceptionnaire). Le statut passera en « Signé ».</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="mb-1.5 block">Nom du signataire *</Label>
            <Input value={signedByInput} onChange={(e) => setSignedByInput(e.target.value)} placeholder="Ex: Andres Martinez" data-testid="bl-signed-by-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignOpen(false)}>Annuler</Button>
            <Button onClick={sign} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="bl-confirm-sign">Confirmer la signature</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 101fr — Dialog Attacher revendeur au BL */}
      <Dialog open={attachDialogOpen} onOpenChange={setAttachDialogOpen}>
        <DialogContent data-testid="bl-attach-reseller-dialog">
          <DialogHeader>
            <DialogTitle>Attacher un revendeur à ce BL</DialogTitle>
            <DialogDescription>
              Utile quand le BL a été créé « à la va vite » sans passer par une commande. Les prix négociés seront appliqués automatiquement.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="mb-1.5 block">Revendeur *</Label>
              <Select
                value={attachForm.reseller_id}
                onValueChange={(v) => setAttachForm({ ...attachForm, reseller_id: v })}
              >
                <SelectTrigger data-testid="attach-reseller-select">
                  <SelectValue placeholder="Sélectionne un revendeur…" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {resellers
                    .filter((r) => !r.archived)
                    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                    .map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                        {r.discount_percent != null && ` · −${r.discount_percent}%`}
                        {r.legal_name && r.legal_name !== r.name && ` (${r.legal_name})`}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={attachForm.apply_prices}
                onChange={(e) => setAttachForm({ ...attachForm, apply_prices: e.target.checked })}
                className="mt-1"
                data-testid="attach-apply-prices"
              />
              <div>
                <div className="text-sm font-medium text-[#1A1C1A]">Appliquer les prix négociés</div>
                <p className="text-[11px] text-[#575A57] mt-0.5">
                  Recalcule chaque ligne avec l'override individuel du produit &gt; remise globale sur prix de cession. Ignore les lignes sans prix négocié applicable.
                </p>
              </div>
            </label>

            {!bl?.transfer_executed && (
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={attachForm.switch_distributor}
                  onChange={(e) => setAttachForm({ ...attachForm, switch_distributor: e.target.checked })}
                  className="mt-1"
                  data-testid="attach-switch-distributor"
                />
                <div>
                  <div className="text-sm font-medium text-[#1A1C1A]">Changer le destinataire vers le distributeur du revendeur</div>
                  <p className="text-[11px] text-[#575A57] mt-0.5">
                    Utile si le BL pointe vers un mauvais client. Refusé si le transfert de stock est déjà exécuté.
                  </p>
                </div>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttachDialogOpen(false)}>Annuler</Button>
            <Button
              onClick={submitAttachReseller}
              disabled={attaching || !attachForm.reseller_id}
              className="bg-[#1F4E3D] hover:bg-[#173B2E]"
              data-testid="bl-confirm-attach-reseller"
            >
              {attaching ? "Attachement…" : "Attacher & recalculer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
